import React from "react";
import { Link, useParams } from "react-router-dom";
import { Monitor, Smartphone, Share, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const steps = [
  {
    title: "iPhone and iPad Safari",
    icon: Share,
    text: "Open Marstick in Safari, tap Share, choose Add to Home Screen, then tap Add. iOS does not allow websites to install apps silently.",
  },
  {
    title: "Android Chrome or Edge",
    icon: Smartphone,
    text: "Open Marstick in Chrome or Edge. If the install banner appears, tap Install. If not, open the browser menu and choose Add to Home screen or Install app.",
  },
  {
    title: "Desktop Chrome or Edge",
    icon: Monitor,
    text: "Open Marstick on desktop and use the install icon in the address bar when shown, or open the browser menu and choose Install Marstick.",
  },
  {
    title: "Firefox and other browsers",
    icon: ShieldCheck,
    text: "Firefox support varies by device and version. Use the browser menu if an Add to Home Screen option is available, or keep Marstick bookmarked.",
  },
];

export default function InstallGuidePage() {
  const { lang = "en" } = useParams();

  return (
    <main className="maritime-container py-8 md:py-14">
      <section className="rounded-[2rem] border border-white/60 bg-slate-950 p-5 text-white shadow-2xl shadow-ocean/20 sm:p-8">
        <p className="text-xs font-black uppercase tracking-[0.24em] text-cyan-200">Mobile app setup</p>
        <h1 className="mt-3 max-w-3xl font-heading text-4xl font-black tracking-tight !text-white md:text-6xl">Install Marstick as a mobile web app</h1>
        <p className="mt-4 max-w-3xl text-base leading-7 text-cyan-50/85">Marstick installs through your browser’s native Progressive Web App flow. It is not a browser extension, ZIP installer, or silent download.</p>
      </section>

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        {steps.map(({ title, icon: Icon, text }) => (
          <Card key={title} className="rounded-[1.5rem] border-white/60 bg-white/90">
            <CardContent className="p-5">
              <Icon className="h-7 w-7 text-primary" aria-hidden="true" />
              <h2 className="mt-3 text-xl font-black">{title}</h2>
              <p className="mt-2 text-sm leading-7 text-slate-600">{text}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="mt-5 rounded-[1.5rem] border-white/60 bg-white/90">
        <CardContent className="p-5">
          <h2 className="text-xl font-black">Important note about extensions</h2>
          <p className="mt-2 text-sm leading-7 text-slate-600">Browser extensions cannot be silently installed by a website. Marstick’s mobile app experience uses PWA installation from Safari, Chrome, Edge, or another supported browser.</p>
          <Button asChild className="mt-4 rounded-full"><Link to={`/${lang}`}>Back to Marstick</Link></Button>
        </CardContent>
      </Card>
    </main>
  );
}